import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { CardGroup, Card, CardBody, Row, Col, Form, FormGroup, Label, Input } from 'reactstrap';
import PhoneInput from 'react-phone-number-input';
import { Mutation, useMutation } from 'react-apollo';
import { FormattedMessage, injectIntl } from 'react-intl';
import { get } from 'lodash-es';
import { CustomDashboardInput, CustomButton } from '../../containers';
import { formatPhoneNumber } from '../../utils/formatPhoneNumber';
import {
  ConfirmActivationChangeModal,
  ConfirmLockChangeModal,
  ConfirmUserDeleteModal,
  ConfirmVerificationChangeModal,
  ConfirmPermissionsChangeModal,
} from '../../components/AdminEditBasicDataModals';
import { validateFormSubmission } from '../../utils/validation';
import { handleRequestError } from '../../constants/errorCodes';
import {
  AdminUpdateUserPhoneNumberMutation,
  AdminUpdateUserEmailMutation,
} from '../../operations/mutations/AdminUpdateUserMutation';
import { renderToaster, SUCCESS_TOASTER, ERROR_TOASTER } from '../../constants/toaster';
import UserPasswordRecoveryRequestMutation from '../../operations/mutations/UserPasswordRecoveryRequestMutation';
import { isHOKEmployee, HOK_ROLES, USER } from '../../constants/roles';
import { AdminSearchForHOKAccountMutation } from '../../operations/mutations/AdminSearchForHOKAccountMutation';
import { AvatarDisplayAndChange } from '../AvatarDisplayAndChange';

export const AdminEditBasicData = injectIntl(props => {
  const dispatch = useDispatch();
  const { user } = props;

  const [searchForHOKAccount, { data, loading: searchLoading }] = useMutation(AdminSearchForHOKAccountMutation, {
    onError: error => handleRequestError(error, dispatch),
    onCompleted: () => setIsVerificationModalOpen(true),
  });

  const [email, setEmail] = useState({
    value: user.email,
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.email" />,
    validation: {
      required: true,
      email: true,
    },
  });

  const [phoneNumber, setPhoneNumber] = useState({
    value: user.phoneNumber,
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.phoneNumber" />,
    validation: {
      required: true,
    },
  });

  const [verified, setVerified] = useState(get(user, 'verified', false));

  const [verificationComment, setVerificationComment] = useState({
    value: user.verificationComment || '',
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.verificationComment" />,
    validation: {
      required: true,
    },
  });
  const [role, setRole] = useState({
    value: user.role || null,
    error: null,
    invalid: false,
    displayName: <FormattedMessage id="inputs.role" />,
    validation: {
      required: true,
      enum: [...HOK_ROLES, null],
    },
  });

  const [accountLocked, setAccountLocked] = useState(get(user, 'accountLocked', false));
  const [accountActive, setAccountActive] = useState(get(user, 'activated', false));

  const [isLockModalOpen, setIsLockModalOpen] = useState(false);
  const toggleLockModal = () => {
    setIsLockModalOpen(isLockModalOpen => !isLockModalOpen);
  };

  const [isActivationModalOpen, setIsActivationModalOpen] = useState(false);
  const toggleActivationModal = () => {
    setIsActivationModalOpen(isActivationModalOpen => !isActivationModalOpen);
  };

  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const toggleVerificationModal = () => {
    setIsVerificationModalOpen(isVerificationModalOpen => !isVerificationModalOpen);
  };

  const [isUserDeleteModalOpen, setIsUserDeleteModalOpen] = useState(false);
  const toggleUserDeleteModal = () => {
    setIsUserDeleteModalOpen(isUserDeleteModalOpen => !isUserDeleteModalOpen);
  };

  const [isPermissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const togglePermissionsModal = () => {
    setPermissionsModalOpen(isPermissionsModalOpen => !isPermissionsModalOpen);
  };

  const isUserEmployee = isHOKEmployee(user);

  return (
    <CardGroup>
      <Card className="hok-card">
        <CardBody>
          <Row className="my-4">
            <Col xs="12">
              <h3>
                <FormattedMessage id="inputs.partnerId" />
              </h3>
            </Col>
            <Col xs="12" sm="6" xl="5">
              <Input id="partnerId" readOnly defaultValue={get(user, 'partnerId', '')} />
            </Col>
          </Row>
          <Row className="mb-4">
            <Col xs="12">
              <h3>
                <FormattedMessage id="inputs.email" />
              </h3>
            </Col>
            <Col xs="12" className="d-flex align-items-end mt-3">
              <Mutation
                mutation={AdminUpdateUserEmailMutation}
                onError={error => handleRequestError(error, dispatch)}
                onCompleted={() =>
                  renderToaster(`successMessages.userEmailUpdated`, SUCCESS_TOASTER, { intlKey: true })
                }>
                {(updatePhoneNumber, { loading }) => {
                  const handleUserEmailChangeSubmit = async event => {
                    event.preventDefault();
                    if (await validateFormSubmission([setEmail])) {
                      updatePhoneNumber({
                        variables: { id: user.id, email: email.value, phoneNumber: user.phoneNumber },
                      });
                    }
                  };

                  return (
                    <Form className="w-100" onSubmit={handleUserEmailChangeSubmit}>
                      <FormGroup row>
                        <Col xs="12" sm="6" xl="5">
                          <Label>
                            <FormattedMessage id="inputs.email" />
                          </Label>
                          <CustomDashboardInput
                            id="email"
                            fieldState={email}
                            onChange={event => {
                              setEmail({ ...email, invalid: false, value: event.target.value });
                            }}
                          />
                        </Col>
                        <Col xs="12" sm="6" xl="7" className="no-label-align-helper mt-3 mt-sm-0">
                          <CustomButton
                            className="hok-outline-btn"
                            block={false}
                            outline
                            translationId="changeEmail"
                            loaderProp={loading}
                          />
                        </Col>
                      </FormGroup>
                    </Form>
                  );
                }}
              </Mutation>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col xs="12">
              <h3>
                <FormattedMessage id="inputs.password" />
              </h3>
            </Col>
            <Col xs="12" className="mt-3">
              <Label>
                <FormattedMessage id="adminEditUserPage.userPassword" />
              </Label>
            </Col>
            <Col xs="12">
              <Mutation
                mutation={UserPasswordRecoveryRequestMutation}
                onError={error => handleRequestError(error, dispatch)}
                onCompleted={() =>
                  renderToaster(`successMessages.userPasswordReset`, SUCCESS_TOASTER, { intlKey: true })
                }>
                {(resetPassword, { loading }) => {
                  return (
                    <CustomButton
                      className="hok-outline-btn"
                      block={false}
                      outline
                      translationId="resetPassword"
                      loaderProp={loading}
                      onClick={() => {
                        resetPassword({ variables: { username: user.email } });
                      }}
                    />
                  );
                }}
              </Mutation>
            </Col>
          </Row>
          <AvatarDisplayAndChange className="mt-5 mb-4" avatar={user.avatar} showChangeButton={false} />
          <Row className="mt-5 mb-4">
            <Col xs="12">
              <h3>
                <FormattedMessage id="userProfilePage.mobilePhone" />
              </h3>
            </Col>
            <Col xs="12" className="d-flex align-items-end mt-3">
              <Mutation
                mutation={AdminUpdateUserPhoneNumberMutation}
                onError={error => handleRequestError(error, dispatch)}
                onCompleted={() =>
                  renderToaster(`successMessages.userPhoneNumberUpdated`, SUCCESS_TOASTER, { intlKey: true })
                }>
                {(updatePhoneNumber, { loading }) => {
                  const handleUserPhoneNumberChangeSubmit = async event => {
                    event.preventDefault();
                    if (await validateFormSubmission([setPhoneNumber])) {
                      updatePhoneNumber({
                        variables: { id: user.id, email: user.email, phoneNumber: phoneNumber.value },
                      });
                    }
                  };

                  return (
                    <Form className="w-100" onSubmit={handleUserPhoneNumberChangeSubmit}>
                      <FormGroup row>
                        <Col xs="12" sm="6" xl="5">
                          <Label>
                            <FormattedMessage id="inputs.phoneNumber" />
                          </Label>
                          <CustomDashboardInput
                            id="phoneNumber"
                            name="mobile phone number"
                            type="tel"
                            maxLength={21}
                            tag={PhoneInput}
                            addInternationalOption={false}
                            fieldState={phoneNumber}
                            numberInputProps={phoneNumber.invalid ? 'is-invalid' : ''}
                            onChange={phone =>
                              setPhoneNumber({ ...phoneNumber, invalid: false, value: formatPhoneNumber(phone) })
                            }
                          />
                        </Col>
                        <Col xs="12" sm="6" xl="7" className="no-label-align-helper mt-3 mt-sm-0">
                          <CustomButton
                            className="hok-outline-btn"
                            block={false}
                            outline
                            translationId="changeNumber"
                            loaderProp={loading}
                          />
                        </Col>
                      </FormGroup>
                    </Form>
                  );
                }}
              </Mutation>
            </Col>
          </Row>
          <Row>
            <Col xs="12">
              <h3>
                <FormattedMessage
                  id={`userProfilePage.${isUserEmployee ? 'employeeStatusTitle' : 'userStatusTitle'}`}
                />
              </h3>
            </Col>
            <Col>
              <Row className="mb-3">
                <Col xs="12" sm="6" xl="5" className="my-3">
                  <Label for="verified">
                    <FormattedMessage id="inputs.validationStatus" />
                  </Label>
                  <Input
                    id="verified"
                    disabled
                    value={props.intl.formatMessage({
                      id: `inputs.${verified ? 'verified' : 'notVerified'}`,
                    })}
                  />
                </Col>
                {verified ? (
                  <Col xs="12" sm="6" xl="5" className="d-flex align-items-end my-3">
                    <div className="w-100">
                      <Label for="verified">
                        <FormattedMessage id="inputs.validationComment" />
                      </Label>
                      <Input id="verificationComment" disabled value={verificationComment.value} />
                    </div>
                  </Col>
                ) : (
                  <Col xs="12" sm="6" xl="7" className="d-flex align-items-end mb-3">
                    <CustomButton
                      className="hok-outline-btn"
                      block={false}
                      outline
                      translationId={isUserEmployee ? 'verifyEmployee' : 'verifyUser'}
                      onClick={() => {
                        if (user.oib) {
                          searchForHOKAccount({ variables: { userId: `${user.id}`, oib: user.oib } });
                        } else {
                          renderToaster(`errors.verificationMissingOib`, ERROR_TOASTER, { intlKey: true });
                        }
                      }}
                      loaderProp={searchLoading}
                    />
                  </Col>
                )}
              </Row>
              <Row className="mb-3">
                <Col xs="12" sm="6" xl="5" className="mb-3">
                  <Label for="active">
                    <FormattedMessage id="inputs.activationStatus" />
                  </Label>
                  <Input
                    id="active"
                    disabled
                    value={props.intl.formatMessage({
                      id: `inputs.${accountActive ? 'activated' : 'deactivate'}`,
                    })}
                  />
                </Col>
                <Col xs="12" sm="6" xl="7" className="no-label-align-helper mb-3">
                  <CustomButton
                    className="hok-outline-btn"
                    block={false}
                    outline
                    translationId={accountActive ? 'deactivate' : 'activate'}
                    onClick={toggleActivationModal}
                  />
                </Col>
                <ConfirmActivationChangeModal
                  isOpen={isActivationModalOpen}
                  toggle={toggleActivationModal}
                  user={user}
                  setAccountActive={setAccountActive}
                  accountActive={accountActive}
                />
              </Row>
              <Row>
                <Col xs="12" sm="6" xl="5" className="mb-3">
                  <Label for="lock">
                    <FormattedMessage id="inputs.lockStatus" />
                  </Label>
                  <Input
                    id="lock"
                    disabled
                    value={props.intl.formatMessage({
                      id: `inputs.${accountLocked ? 'locked' : 'activated'}`,
                    })}
                  />
                </Col>
                <Col xs="12" sm="6" xl="7" className="no-label-align-helper mb-3">
                  <CustomButton
                    className="hok-outline-btn"
                    block={false}
                    outline
                    translationId={accountLocked ? 'activate' : 'lock'}
                    onClick={toggleLockModal}
                  />
                </Col>
                <ConfirmLockChangeModal
                  isOpen={isLockModalOpen}
                  toggle={toggleLockModal}
                  user={user}
                  setAccountLocked={setAccountLocked}
                  accountLocked={accountLocked}
                />
              </Row>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col xs="12">
              <h3>
                <FormattedMessage
                  id={`userProfilePage.${isUserEmployee ? 'employeePermissions' : 'userPermissions'}`}
                />
              </h3>
            </Col>
          </Row>
          <Row>
            <Col xs="12" sm="6" xl="5" className="mt-3 mb-3">
              <Label for="lock">
                <FormattedMessage id="userProfilePage.administrativePermissions" />
              </Label>
              <div className="hok-select">
                <CustomDashboardInput
                  className="input-uppercase"
                  type="select"
                  id="role"
                  value={role.value || 'null'}
                  fieldState={role}
                  onChange={event => {
                    const value = event.target.value === 'null' ? null : event.target.value;
                    setRole({ ...role, invalid: false, value: value });
                  }}>
                  {HOK_ROLES.map(role => (
                    <option key={role} value={role}>
                      {props.intl.formatMessage({ id: `roles.${role}` })}
                    </option>
                  ))}
                  <option key={USER} value="null">
                    {props.intl.formatMessage({ id: `roles.${USER}` })}
                  </option>
                </CustomDashboardInput>
              </div>
            </Col>
            <Col xs="12" sm="6" xl="7" className="d-flex align-items-end mb-3">
              <CustomButton
                className="hok-outline-btn"
                block={false}
                outline
                translationId="applyPermissions"
                onClick={togglePermissionsModal}
              />
            </Col>
            <ConfirmPermissionsChangeModal
              isOpen={isPermissionsModalOpen}
              toggle={togglePermissionsModal}
              user={user}
              role={role}
            />
          </Row>
          <Row className="mb-2 mt-4">
            <Col xs="12">
              <h3>
                <FormattedMessage id={`userProfilePage.${isUserEmployee ? 'removeEmployee' : 'removeUser'}`} />
              </h3>
            </Col>
          </Row>
          <Row>
            <Col xs="12" sm="6" xl="7" className="pt-0 no-label-align-helper">
              <CustomButton
                className="hok-button hok-dashboard-btn"
                block={false}
                translationId="delete"
                onClick={toggleUserDeleteModal}
              />
            </Col>
            <ConfirmUserDeleteModal isOpen={isUserDeleteModalOpen} toggle={toggleUserDeleteModal} user={user} />
          </Row>
        </CardBody>
      </Card>
      <ConfirmVerificationChangeModal
        isOpen={isVerificationModalOpen}
        toggle={toggleVerificationModal}
        partnerData={data}
        verified={verified}
        setVerified={setVerified}
        verificationComment={verificationComment}
        setVerificationComment={setVerificationComment}
        user={user}
      />
    </CardGroup>
  );
});
